import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CommandPaletteContext } from "@/app/dashboard/commandPaletteContext";
import { T } from "@/shared/ui/DesignLib";
import { useAppContext } from "@/app/providers/AppContext";
import { Loader2 } from "lucide-react";
import type { TickerSuggestion } from "@/shared/lib/types";

interface TickerSearchResponse {
  results?: TickerSuggestion[];
}

// ─── Types ──────────────────────────────────────────────────────────────────
type CommandCategory = "Tickers" | "Actions" | "Pages";
const COMMAND_CATEGORY_ORDER: CommandCategory[] = ["Tickers", "Actions", "Pages"];

interface CommandItem {
  id: string;
  label: string;
  subLabel?: string;
  category: CommandCategory;
  icon: React.ReactNode;
  action: (
    nav: ReturnType<typeof useNavigate>,
    callbacks: PaletteCallbacks,
    originPath: string,
  ) => void;
}

interface PaletteCallbacks {
  onCreateWatch?: () => void;
}

// ─── SVG Icons (inline, no deps) ───────────────────────────────────────────
const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.dim} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const TickerIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
    <polyline points="16 7 22 7 22 13" />
  </svg>
);

const ActionIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.amber} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const PageIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.mid} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

// ─── Command Registry ───────────────────────────────────────────────────────
const TICKERS = ["AAPL", "MSFT", "GOOGL", "AMZN", "NVDA", "TSLA", "META", "SPY", "QQQ", "IWM", "GLD"];

const buildCommands = (): CommandItem[] => {
  const tickers: CommandItem[] = TICKERS.map((sym) => ({
    id: `ticker-${sym}`,
    label: sym,
    category: "Tickers",
    icon: <TickerIcon />,
    action: (nav, _callbacks, originPath) =>
      nav(`/ticker/${sym}`, { state: { from: originPath } }),
  }));

  const actions: CommandItem[] = [
    {
      id: "action-create-watch",
      label: "Create New Watch",
      category: "Actions",
      icon: <ActionIcon />,
      action: (nav) => nav("/portal/watches/new"),
    },
    {
      id: "action-dashboard",
      label: "Go to Dashboard",
      category: "Actions",
      icon: <ActionIcon />,
      action: (nav) => nav("/portal"),
    },
    {
      id: "action-settings",
      label: "Go to Settings",
      category: "Actions",
      icon: <ActionIcon />,
      action: (nav) => nav("/portal/account"),
    },
    {
      id: "action-connector",
      label: "Open Connector",
      category: "Actions",
      icon: <ActionIcon />,
      action: (nav) => nav("/portal/connector"),
    },
  ];

  const pages: CommandItem[] = [
    {
      id: "page-landing",
      label: "Landing Page",
      category: "Pages",
      icon: <PageIcon />,
      action: (nav) => nav("/"),
    },
    {
      id: "page-privacy",
      label: "Privacy Policy",
      category: "Pages",
      icon: <PageIcon />,
      action: (nav) => nav("/privacy"),
    },
    {
      id: "page-pricing",
      label: "Pricing",
      category: "Pages",
      icon: <PageIcon />,
      action: (nav) => nav("/#plans"),
    },
  ];

  return [...tickers, ...actions, ...pages];
};

const ALL_COMMANDS = buildCommands();

// ─── Fuzzy Match ────────────────────────────────────────────────────────────
function fuzzyMatch(query: string, text: string): boolean {
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  if (t.includes(q)) return true;
  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++;
  }
  return qi === q.length;
}

// ─── Category Badge ─────────────────────────────────────────────────────────
const categoryColor: Record<CommandCategory, string> = {
  Tickers: T.primary,
  Actions: T.amber,
  Pages: T.mid,
};

// ─── Component ──────────────────────────────────────────────────────────────
interface CommandPaletteProps {
  onCreateWatch?: () => void;
}

export const CommandPaletteProvider: React.FC<{ children: React.ReactNode } & CommandPaletteProps> = ({
  children,
  onCreateWatch,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  return (
    <CommandPaletteContext.Provider value={{ open, close, isOpen }}>
      {children}
      {isOpen ? (
        <CommandPaletteModal isOpen onClose={close} onCreateWatch={onCreateWatch} />
      ) : null}
    </CommandPaletteContext.Provider>
  );
};

interface CommandPaletteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateWatch?: () => void;
}

const CommandPaletteModal: React.FC<CommandPaletteModalProps> = ({ isOpen, onClose, onCreateWatch }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const originPath = `${location.pathname}${location.search}`;
  const { api } = useAppContext();
  const [query, setQuery] = useState("");
  const [dynamicTickers, setDynamicTickers] = useState<CommandItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // ─── Global Cmd+K listener ──────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        if (isOpen) {
          onClose();
        } else {
          // We use the context open, but since this component only renders
          // when the provider is up, we toggle via the parent.
          // Actually, we need to open from the provider level.
          // This is handled by the provider's global listener below.
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  // ─── Dynamic Ticker Search ──────────────────────────────────────────
  useEffect(() => {
    const search = async () => {
      const q = query.trim();
      if (!q) {
        setDynamicTickers([]);
        setIsSearching(false);
        return;
      }
      setIsSearching(true);
      try {
        const data = await api<TickerSearchResponse>("GET", `/api/tickers/search?query=${q}`);
        const results: CommandItem[] = (data.results || []).map((s) => ({
          id: `dynamic-${s.ticker}`,
          label: s.ticker,
          subLabel: s.name,
          category: "Tickers" as const,
          icon: <TickerIcon />,
          action: (nav, _callbacks, currentOriginPath) =>
            nav(`/ticker/${s.ticker}`, { state: { from: currentOriginPath } }),
        }));
        setDynamicTickers(results);
      } catch {
        // Dynamic ticker search is best-effort; static commands remain available.
      } finally {
        setIsSearching(false);
      }
    };

    const timer = setTimeout(search, 300);
    return () => clearTimeout(timer);
  }, [query, api]);

  // The modal is mounted fresh for every open, so local search and selection
  // state start clean without an effect-driven reset.
  useEffect(() => {
    if (!isOpen) return;
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  // ─── Filtered results ───────────────────────────────────────────────
  const filteredActions = ALL_COMMANDS.filter((cmd) =>
    cmd.category !== "Tickers" &&
    (!query.trim() || fuzzyMatch(query.trim(), cmd.label))
  );

  const displayTickers = query.trim() ? dynamicTickers : ALL_COMMANDS.filter(c => c.category === "Tickers");

  const filtered = [...displayTickers, ...filteredActions];

  // Group by category for rendering
  const grouped: { category: CommandCategory; items: (CommandItem & { flatIndex: number })[] }[] = [];
  let flatIdx = 0;
  for (const cat of COMMAND_CATEGORY_ORDER) {
    const items = filtered.filter((c) => c.category === cat);
    if (items.length > 0) {
      grouped.push({
        category: cat,
        items: items.map((item) => ({ ...item, flatIndex: flatIdx++ })),
      });
    }
  }
  const totalCount = flatIdx;
  const activeIndex = totalCount === 0
    ? 0
    : Math.min(selectedIndex, totalCount - 1);

  // Scroll selected into view
  useEffect(() => {
    const el = itemRefs.current.get(activeIndex);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  // ─── Activate selected ─────────────────────────────────────────────
  const activate = (index: number) => {
    let fi = 0;
    for (const cat of COMMAND_CATEGORY_ORDER) {
      const items = filtered.filter((c) => c.category === cat);
      for (const item of items) {
        if (fi === index) {
          item.action(navigate, { onCreateWatch }, originPath);
          onClose();
          return;
        }
        fi++;
      }
    }
  };

  // ─── Keyboard nav ──────────────────────────────────────────────────
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((activeIndex + 1) % Math.max(totalCount, 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((activeIndex - 1 + Math.max(totalCount, 1)) % Math.max(totalCount, 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      activate(activeIndex);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.50)",
        zIndex: 50,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "min(20vh, 160px)",
        animation: "fadeIn .15s ease",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 520,
          background: T.bg,
          border: `1px solid ${T.border}`,
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
          animation: "zoomIn .2s cubic-bezier(.34,1.4,.64,1) both",
        }}
        onKeyDown={handleKeyDown}
      >
        {/* ─── Search Input ─────────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "14px 16px",
            borderBottom: `1px solid ${T.borderSoft}`,
          }}
        >
          <SearchIcon />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            placeholder="Search tickers, commands..."
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              outline: "none",
              color: T.text,
              fontFamily: T.mono,
              fontSize: ".82rem",
              letterSpacing: ".02em",
            }}
            spellCheck={false}
            autoComplete="off"
          />
          {isSearching && (
            <Loader2 size={12} className="animate-spin" color={T.primary} />
          )}
          <kbd
            style={{
              fontFamily: T.mono,
              fontSize: ".55rem",
              color: T.dim,
              background: T.card2,
              borderRadius: 4,
              padding: "2px 6px",
              border: `1px solid ${T.border}`,
            }}
          >
            ESC
          </kbd>
        </div>

        {/* ─── Results ──────────────────────────────────────────── */}
        <div
          ref={listRef}
          style={{
            maxHeight: 320,
            overflowY: "auto",
            padding: "6px 0",
          }}
        >
          {totalCount === 0 && (
            <div
              style={{
                padding: "24px 16px",
                textAlign: "center",
                fontFamily: T.mono,
                fontSize: ".72rem",
                color: T.dim,
              }}
            >
              No results found
            </div>
          )}

          {grouped.map((group) => (
            <div key={group.category}>
              {/* Category Header */}
              <div
                style={{
                  padding: "10px 16px 4px",
                  fontFamily: T.mono,
                  fontSize: ".55rem",
                  fontWeight: 600,
                  letterSpacing: ".1em",
                  textTransform: "uppercase",
                  color: T.dim,
                }}
              >
                {group.category}
              </div>

              {/* Items */}
              {group.items.map((item) => {
                const isActive = item.flatIndex === activeIndex;
                return (
                  <div
                    key={item.id}
                    ref={(el) => {
                      if (el) itemRefs.current.set(item.flatIndex, el);
                    }}
                    onClick={() => {
                      setSelectedIndex(item.flatIndex);
                      activate(item.flatIndex);
                    }}
                    onMouseEnter={() => setSelectedIndex(item.flatIndex)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 16px",
                      cursor: "pointer",
                      background: isActive ? "rgba(0,184,219,0.1)" : "transparent",
                      borderLeft: isActive ? `2px solid ${T.primary}` : "2px solid transparent",
                      transition: "background .1s, border-color .1s",
                    }}
                  >
                    <span style={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
                      {item.icon}
                    </span>
                    <span
                      style={{
                        flex: 1,
                        fontFamily: T.mono,
                        fontSize: ".74rem",
                        color: T.text,
                        letterSpacing: ".02em",
                      }}
                    >
                      {item.label}
                    </span>
                    {item.subLabel && !isActive && (
                      <span style={{
                        fontFamily: T.mono,
                        fontSize: ".55rem",
                        color: T.dim,
                        marginLeft: 8,
                        maxWidth: 180,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}>
                        {item.subLabel}
                      </span>
                    )}
                    {isActive && item.subLabel && (
                      <span style={{
                        fontFamily: T.mono,
                        fontSize: ".55rem",
                        color: T.text,
                        opacity: 0.6,
                        marginLeft: 8,
                      }}>
                        {item.subLabel}
                      </span>
                    )}
                    <span
                      style={{
                        fontFamily: T.mono,
                        fontSize: ".5rem",
                        fontWeight: 600,
                        letterSpacing: ".08em",
                        textTransform: "uppercase",
                        color: categoryColor[item.category],
                        opacity: 0.5,
                        background: `${categoryColor[item.category]}10`,
                        borderRadius: 4,
                        padding: "2px 6px",
                      }}
                    >
                      {item.category}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* ─── Footer ───────────────────────────────────────────── */}
        <div
          style={{
            padding: "8px 16px",
            borderTop: `1px solid ${T.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            fontFamily: T.mono,
            fontSize: ".52rem",
            color: T.dim,
            letterSpacing: ".04em",
          }}
        >
          <span>
            <kbd style={kbdStyle}>↑</kbd>
            <kbd style={kbdStyle}>↓</kbd> Navigate
          </span>
          <span>
            <kbd style={kbdStyle}>↵</kbd> Select
          </span>
          <span>
            <kbd style={kbdStyle}>esc</kbd> Close
          </span>
        </div>
      </div>
    </div>
  );
};

const kbdStyle: React.CSSProperties = {
  fontFamily: T.mono,
  fontSize: ".52rem",
  padding: "1px 4px",
  borderRadius: 3,
  border: `1px solid ${T.border}`,
  background: T.card2,
  marginRight: 3,
};

// ─── Standalone Component (wraps provider for simple drop-in) ───────────────
const CommandPalette: React.FC<CommandPaletteProps> = ({ onCreateWatch }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Global Cmd+K listener
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return isOpen ? (
    <CommandPaletteModal
      isOpen
      onClose={() => setIsOpen(false)}
      onCreateWatch={onCreateWatch}
    />
  ) : null;
};

export default CommandPalette;
